import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { uniqBy } from 'lodash'

import {
  activeCellClass,
  Alert,
  Button,
  Cell,
  Checkbox,
  hasStickyColumnStyle,
  hasValue,
  Icon,
  isEqual,
  ListLoading,
  Popover,
  Row,
  SelectDisplay,
  StickyTable,
  StickyTableContainer,
  Tooltip,
} from '../../../module'
import { debounce } from '../../../services/HelperServiceTyped'
import { getDataGroups, useShowStickyClasses } from '../helperFunctions'
import {
  checkedData,
  useHandleCheck,
  useIsChecked,
} from '../helperCheckboxFunctions'
import type {
  ConfigItemType,
  DesktopTableProps,
  groupAccordionType,
  GroupHeader,
} from '../StandardTable.models'
import TableFilter from '../TableFilter/TableFilter'
import TableGradient from '../TableGradient'
import NestedRow from './../NestedRow'
import UnsortedColumn from '../../UnsortedColumn/UnsortedColumn'
import { backgroundColor, color } from '../StandardTable'
import styles from '../_table.module.scss'
import SortColumn from '../../SortColumn/SortColumn'
import CustomizeTableColumns from '../CustomizeTableColumns/CustomizeTableColumns'
import { c } from '../../../translations/LibraryTranslationService'
import { type SelectDisplayOption } from '../../Selects/SelectDisplay/SelectDisplay'

// NOTE: this method adjusts the right visual border of the end column when customColumns && nestedRows are used together
// If rightStickyCountFromConfig is set that means padding shouldn't be added to the last cell of Custom or Nested Columns
export const getCustomColumnStickyStyles = (
  nthCellFromRight: number,
  stickyRightCount: number,
  rightStickyCountFromConfig?: number /** This prop specifies the number of sticky columns on the right side, as defined in the table config.*/,
) => {
  return nthCellFromRight === stickyRightCount && !rightStickyCountFromConfig
    ? `${styles.firstStickyCellRight} ${styles.extraLeftPadding}`
    : ''
}

const DesktopTable = <
  DataItem extends {
    groupDataKey?: string
  },
  ConfigItem extends ConfigItemType<DataItem, Record<string, unknown>>,
  FilterOption extends Record<string, unknown>,
>({
  activeFilters,
  checkboxSelectOptions,
  config,
  customColumnProps,
  customHeight,
  customWidth,
  data,
  dataKey,
  disableCheckboxes,
  expandedAccordionContent,
  enableCheckAll = false,
  disableCheckAll = false,
  getData,
  groups,
  hasExpandableContent,
  handleCheckedBoxes,
  hasCheckboxes,
  hasData,
  hasMore,
  headerChildren,
  isDefaultCheckedAll = false,
  isResetCheckboxes,
  loading,
  localStorageName,
  nestedDataKey,
  nestedRowProps,
  removeFilters,
  shortListLoading,
  showGroups,
  sort,
  sortBy,
  stickyTableConfig,
  successStatus,
  tableHeaderProps,
  totalCheckboxOptions,
  totalRowKey,
  twoLineLabel,
  widthOffset,
}: DesktopTableProps<
  DataItem,
  ConfigItem,
  FilterOption
>): React.JSX.Element => {
  const infiniteLoadRef = useRef<HTMLDivElement>(null),
    tableRef = useRef<HTMLDivElement>(null),
    tableWidth = tableRef.current?.clientWidth,
    innerTableWidth = tableRef.current?.querySelector(
      '.sticky-table-table',
    )?.clientWidth

  const totalRowKeyRef = useRef<HTMLDivElement>(null)

  const headerStyle = (isStickyLeftColumn: boolean, hasTotalRow: boolean) => ({
    position: 'sticky',
    top: hasTotalRow
      ? totalRowKeyRef.current
        ? totalRowKeyRef.current.clientHeight + 57
        : 97 /** We need a dynamic height here because the "Total" row can vary in height for each table implementation. We then add 57 because that is he height of the sticky header. The default value, if totalRowKeyRef is undefined, is 97 because we take 57 + 40 (the min height we want for the "Total" row). */
      : '57px' /** The height of the sticky header is set to 57px in full desktop view */,
    zIndex: isStickyLeftColumn ? 4 : 3,
  })

  const [selectedOption, setSelectedOption] = useState<SelectDisplayOption>()
  const [displayData, setDisplayData] = useState<GroupHeader<DataItem>[]>(data),
    [selectedNestedRowData, setSelectedNestedRowData] = useState<
      DataItem[keyof DataItem][]
    >(
      nestedRowProps?.initiallyExpandedRows
        ? nestedRowProps?.initiallyExpandedRows
        : [],
    )

  // Expandable row based on accordion logic
  const expandedAccordionContentRef = useRef<HTMLTableCellElement | null>(null)
  const [expandedContentWidth, setExpandedContentWidth] =
    useState<number>(10000) // 10000 is chosen as a default as choosing 0 causes issues if consuming app renders any graphs where SVGs don't resize if its initially 0.
  // Also we want to intentionally make the width bigger than the screen size before calculating the width.
  const expandableAccordionId = 'expandableContentBtn'
  // This useEffect needs to run on any possible re-render to ensure the expanded content width is calculated correctly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Had to get by ID as ref is not getting initialized due to conditional rendering of button below.
    if (tableRef.current && expandedAccordionContentRef.current) {
      const expRowWidth =
          expandedAccordionContentRef.current.getBoundingClientRect().width ??
          0,
        tableWidth = tableRef.current.getBoundingClientRect().width ?? 0
      if (expRowWidth > tableWidth) {
        // 5px is added to accomodate for borders and shadow to be not cut off from overflow. 32 = 16 padding on both sides.
        setExpandedContentWidth(tableWidth - 5 - 32)
      }
    }
  })

  // State to maintain collapsed groups

  const [collapsedGroups, setCollapsedGroups] = useState<Array<string>>([])
  const [isAccordionClicked, setIsAccordionClicked] = useState<boolean>(false)
  const mounted = useRef(true)

  const [state, setState] = useState({
      showStickyClasses: false,
    }),
    { showStickyClasses } = state,
    stickyTableHeaderCount = () => {
      let count = 1
      // order matters here; if totalRowKey is present, it should be used first
      if (totalRowKey) {
        return 2
      }

      if (showGroups) return 1 // showGroups should override any header config that is present

      if (stickyTableConfig?.header) {
        count = stickyTableConfig.header
      }
      return count
    }

  const customColumnStickyStyles = (
    nthCellFromRight: number,
    stickyRightCount: number,
  ) =>
    getCustomColumnStickyStyles(
      nthCellFromRight,
      stickyRightCount,
      stickyTableConfig?.right,
    )

  ///////////////////////////////////////////////////////////////////////////
  // CHECKBOX functionality
  ///////////////////////////////////////////////////////////////////////////
  const [checkedBoxes, setCheckedBoxes] = useState<
      (DataItem & { checked?: boolean })[]
    >([]),
    [checkAll, setCheckAll] = useState(isDefaultCheckedAll),
    [checkAllLoaded, setCheckAllLoaded] = useState(isDefaultCheckedAll),
    [makeCallout, setMakeCallout] = useState<'' | 'single' | 'checkAll'>('')

  const handleCheck = useHandleCheck({
    checkedBoxes,
    setCheckedBoxes,
    setDisplayData,
    data,
    dataKey,
    totalRowKey,
    setCheckAll,
    setCheckAllLoaded,
    enableCheckAll,
    setMakeCallout,
    mounted: mounted.current,
  })

  const { someChecked, allChecked } = useMemo(() => {
    const { someChecked, allChecked } = checkedData({
      data,
      checkedBoxes,
      totalRowKey,
      dataKey,
    })
    if (allChecked) {
      setCheckAllLoaded(true)
    }
    return { someChecked, allChecked }
  }, [checkedBoxes, data, dataKey, totalRowKey])

  const checkBoxesHaveTotalRowKey = checkedBoxes.some(
    (dataItem) => totalRowKey && dataItem[totalRowKey],
  )
  // This is temporary fix until we refactor the standard table
  const resetCheckboxes = useCallback(() => {
    setCheckAllLoaded(false)
    setCheckedBoxes([])
    enableCheckAll && setCheckAll(false)
  }, [enableCheckAll])

  useEffect(() => {
    if (isResetCheckboxes) {
      resetCheckboxes()
    }
  }, [isResetCheckboxes, resetCheckboxes])

  useEffect(() => {
    if (isDefaultCheckedAll) {
      setCheckedBoxes(data?.filter((dataItem) => !dataItem[totalRowKey]))
    }
  }, [data, isDefaultCheckedAll, totalRowKey])

  useEffect(() => {
    // must wait for checkboxes to update on state before making call to handleCheckedBoxes
    if (makeCallout) {
      makeCallout === 'checkAll' &&
        handleCheckedBoxes?.(checkedBoxes, checkAllLoaded)
      makeCallout === 'single' && handleCheckedBoxes?.(checkedBoxes)
      setMakeCallout('')
    }
  }, [checkAllLoaded, checkedBoxes, handleCheckedBoxes, makeCallout])

  // Set checkAll when all items are loaded and checkAllLoaded is true
  useEffect(() => {
    // If all currently loaded items are selected and we've loaded all items
    if (checkAllLoaded && !hasMore && !checkAll && !loading) {
      setCheckAll(true)
    }
  }, [checkAllLoaded, hasMore, checkAll, loading])

  const handleCheckAllOrNone = (checkAll: boolean) => {
    setCheckAllLoaded(checkAll)
    const updatedCheckedBoxes = checkAll ? [...data] : []
    setCheckedBoxes(updatedCheckedBoxes?.filter((item) => !item[totalRowKey]))
    setDisplayData((prevData) =>
      prevData.map((item) => ({
        ...item,
        checked: checkAll,
      })),
    )
    handleCheckedBoxes?.(updatedCheckedBoxes)
  }

  const isChecked = useIsChecked({ checkedBoxes, dataKey })

  const isShowStickyClasses = useShowStickyClasses({
    innerTableWidth: innerTableWidth ?? 0,
    tableContainerWidth: tableWidth ?? 0,
  })

  const getMoreData = useCallback(() => {
    getData?.()
    const allOptionsSelected = enableCheckAll && checkAll
    !allOptionsSelected && setCheckAllLoaded(false)
  }, [checkAll, enableCheckAll, getData])

  const CheckAllBanner = () => {
    return checkAll ? (
      <div className={styles.displayFlex}>
        <span
          className={`${styles.padding16} ${styles.fwBold} ${styles.paddingRight16}`}
        >
          {`${c('all')} ${totalCheckboxOptions ?? ''} ${c('selected')}.`}
        </span>
        <Button
          as='button'
          styleType='text-blue'
          onClick={() => {
            setCheckAll(false)
            handleCheckAllOrNone(false)
          }}
        >
          {c('clearSelection')}
        </Button>
      </div>
    ) : (
      <div className={styles.displayFlex}>
        <span
          className={`${styles.fwBold} ${styles.padding16}`}
        >{`${checkedBoxes.length} ${c('selected')}.`}</span>
        <span
          className={`${styles.paddingX4} ${styles.fwRegular} ${styles.paddingRight16}`}
        >
          {`(${c('allCurrentlyLoaded')})`}
        </span>
        <Button
          as='button'
          styleType='text-blue'
          onClick={() => {
            setCheckAll(true)
            handleCheck({
              checkboxValue: true,
              checkedItemData: data[0],
              name: 'all',
            })
          }}
        >
          {`${c('selectAll')} ${totalCheckboxOptions ?? ''}`}
        </Button>
      </div>
    )
  }

  useEffect(() => {
    // Handler to call on window resize
    const handleResize = debounce(() => {
      if (tableWidth && innerTableWidth) {
        setState((prevState) => ({
          ...prevState,
          showStickyClasses: isShowStickyClasses,
        }))
      }
    }, 250)
    window.addEventListener('resize', handleResize)
    handleResize()
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [hasData, config, isShowStickyClasses, innerTableWidth, tableWidth])

  useEffect(() => {
    if (!loading && infiniteLoadRef.current && hasMore && successStatus) {
      const PAGINATION_VISIBILITY_THRESHOLD = 0.1 // 10% visibility threshold for pagination
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.intersectionRatio > 0) {
              getMoreData()
            }
          })
        },
        { threshold: PAGINATION_VISIBILITY_THRESHOLD },
      )

      observer.observe(infiniteLoadRef.current)

      return () => {
        observer.disconnect()
      }
    }
    // loading dependency was needed for a corner case encountered,
    // when we select all checkboxes, then the observer was
    // unable to update the value and infiniteRef.current remains NULL forever
  }, [hasMore, successStatus, getData, loading, getMoreData])

  useEffect(() => {
    /** SET DATA GROUPS WITH VALUES */
    if (showGroups && groups) {
      const groupData: DataItem[] = getDataGroups(
        data,
        groups,
        { hasCheckboxes, checkedBoxes },
        totalRowKey,
      )

      setDisplayData(groupData)
    } else {
      setDisplayData(data)
    }
  }, [data, groups, showGroups, totalRowKey, checkedBoxes, hasCheckboxes])

  const previousData = useRef(data)
  const previousSortBy = useRef(sortBy)

  useEffect(() => {
    // check new data for checkboxes previously selected to update this component state
    const dataRecordOne: DataItem & { checked?: boolean } = data?.[0]
    if (dataRecordOne?.checked !== undefined) {
      if (data?.length && hasCheckboxes) {
        let noCheckedValues = 0
        const initialCheckedBoxes = data.filter(
          (item: DataItem & { checked?: boolean }) => {
            if (item && !('checked' in item)) {
              noCheckedValues++
            }
            return item?.checked
          },
        )
        if (data?.length !== noCheckedValues) {
          const checkedBoxesWithoutTotalRowKey = initialCheckedBoxes.filter(
            (item) => !item[totalRowKey],
          )
          const checkedBoxesChanged = !isEqual(
            checkedBoxesWithoutTotalRowKey,
            checkedBoxes,
          )
          checkedBoxesChanged && setCheckedBoxes(checkedBoxesWithoutTotalRowKey)
        }
      }

      // when data changes, check if there are items and if all items are checked; if so, update the checkAll state
      setCheckAllLoaded(
        data?.length > 0 &&
          data.every((item: DataItem & { checked?: boolean }) => item?.checked),
      )
    }

    if (enableCheckAll) {
      let hasNewData = true
      if (!isEqual(data, previousData.current)) {
        // Check if only the sort changed
        const onlySortChanged = !isEqual(sortBy, previousSortBy.current)

        if (!onlySortChanged || !checkAll) {
          const wasAdditionalDataLoaded = previousData.current.every(
            (prevItem) => data.some((newItem) => isEqual(prevItem, newItem)),
          )

          // Only reset if it's not just a sort change and no additional data was loaded
          if (!wasAdditionalDataLoaded) {
            resetCheckboxes()
            hasNewData = false
          }
        }

        previousData.current = data
        previousSortBy.current = sortBy
      }

      // if checkAll is true then all new data should be selected
      if (hasNewData && checkAll) {
        const updatedCheckedBoxes = uniqBy([...checkedBoxes, ...data], dataKey)
        !isEqual(updatedCheckedBoxes, checkedBoxes) &&
          setCheckedBoxes(updatedCheckedBoxes)
      }
    }
  }, [
    checkAll,
    checkedBoxes,
    data,
    dataKey,
    hasCheckboxes,
    enableCheckAll,
    resetCheckboxes,
    totalRowKey,
    sortBy,
  ])

  const handleNestedData = (dataItem: DataItem, isOpen: boolean) => {
    if (nestedDataKey) {
      const selectedNestedRowDataCopy = [...selectedNestedRowData]
      const index = selectedNestedRowDataCopy?.indexOf(dataItem[nestedDataKey])
      if (index !== -1) {
        selectedNestedRowDataCopy?.splice(index, 1)
      } else {
        selectedNestedRowDataCopy?.push(dataItem[nestedDataKey])
      }
      if (!isOpen) {
        nestedRowProps?.getNestedData(dataItem[nestedDataKey])
      } else {
        nestedRowProps?.removeNestedData?.(dataItem[nestedDataKey])
      }
      setSelectedNestedRowData(selectedNestedRowDataCopy)
    }
  }

  const stickyRightCount = useMemo(() => {
    let stickyCount: number = stickyTableConfig?.right ?? 0

    stickyCount +=
      nestedRowProps || customColumnProps || hasExpandableContent ? 1 : 0

    return stickyCount
  }, [
    customColumnProps,
    hasExpandableContent,
    nestedRowProps,
    stickyTableConfig?.right,
  ])

  const getStickyLeftCount = () => {
    let stickyCount: number = stickyTableConfig?.left ?? 1
    stickyCount += hasCheckboxes ? 1 : 0
    return stickyCount
  }

  useEffect(() => {
    // Initial expanded/collapsed groups maintaining in component state
    if (showGroups && !isAccordionClicked) {
      const collapsedGroupList = groups
        ?.filter(
          (group) =>
            group?.groupAccordion?.groupKey &&
            group?.groupAccordion?.isCollapsed,
        )
        .map((group) =>
          group.groupAccordion?.groupKey ? group.groupAccordion?.groupKey : '',
        )

      setCollapsedGroups(collapsedGroupList ?? [])
    } else if (loading) {
      setCollapsedGroups([])
    }
  }, [groups, showGroups, loading, isAccordionClicked])

  // Update expanded/collapsed accordion
  const updateAccordion = (groupName: string) => {
    setIsAccordionClicked(true)
    if (typeof groupName === 'string' && collapsedGroups?.includes(groupName)) {
      const filteredGroups = collapsedGroups?.filter(
        (item) => item !== groupName,
      )
      setCollapsedGroups(filteredGroups)
    } else if (typeof groupName === 'string') {
      setCollapsedGroups((collapsedGroups) => [
        ...(collapsedGroups ? collapsedGroups : ''),
        groupName,
      ])
    }
  }

  const equalWidthAmount = useMemo(
    () =>
      tableWidth &&
      (tableWidth -
        (customColumnProps ? 36 : 0) -
        (nestedRowProps ? 32 : 0) -
        (hasCheckboxes ? 42 : 0)) /
        config?.length,
    [
      config?.length,
      tableWidth,
      customColumnProps,
      nestedRowProps,
      hasCheckboxes,
    ],
  )

  return (
    <div className={showStickyClasses ? styles.stickyClasses : ''}>
      {enableCheckAll && checkAllLoaded && !someChecked && hasMore ? (
        <Alert type='info' text={<CheckAllBanner />} />
      ) : null}
      <StickyTableContainer
        hasData={hasData}
        loading={loading}
        customHeight={customHeight}
        customWidth={customWidth}
        widthOffset={widthOffset}
      >
        {/* TODO: move filters to the top Table component once the mobile version of TableFilter has been designed. */}
        {!tableHeaderProps && activeFilters && removeFilters && (
          <TableFilter activeFilters={activeFilters} remove={removeFilters} />
        )}
        <StickyTable
          stickyHeaderCount={stickyTableHeaderCount()}
          leftStickyColumnCount={getStickyLeftCount()}
          rightStickyColumnCount={stickyRightCount}
          borderWidth={0}
          wrapperRef={tableRef}
          className={hasData ? styles.showScrollbar : ''}
        >
          {/*************************************************************************************/}
          {/************************************** HEADER ROW ***********************************/}
          {/*************************************************************************************/}
          <Row className={`${styles.stickyTableHeader} ${styles.headerRow}`}>
            {/************************************* CHECKBOX ************************************/}
            {hasCheckboxes ? (
              <Cell className={styles.checkboxWidth}>
                <div className={styles.selectCheckBoxHeader}>
                  <Checkbox
                    name='checkbox'
                    stateName={enableCheckAll ? 'allLoaded' : 'all'}
                    checked={checkAll || allChecked}
                    partialChecked={someChecked}
                    callout={(name, value) =>
                      handleCheck({
                        checkboxValue: value,
                        checkedItemData: data[0],
                        name,
                      })
                    }
                    label=''
                    hideLabel
                    disabled={disableCheckAll}
                  />
                  {checkboxSelectOptions ? (
                    <Popover
                      noPadding
                      position='bottom'
                      popoverContent={({ setVisible }) => (
                        <SelectDisplay
                          options={
                            checkboxSelectOptions.options ?? [
                              { name: 'all', label: c('all') },
                              { name: 'none', label: c('none') },
                            ]
                          }
                          callout={(option) => {
                            setSelectedOption(option)
                            checkboxSelectOptions.callout(option)
                            setVisible(false)
                            if (option.name === 'all') {
                              handleCheckAllOrNone(true)
                            } else if (option.name === 'none') {
                              handleCheckAllOrNone(false)
                            }
                          }}
                          active={
                            checkboxSelectOptions?.active
                              ? (checkboxSelectOptions.active as keyof SelectDisplayOption)
                              : selectedOption
                                ? (selectedOption?.name === 'all' ||
                                    selectedOption.name === 'none') &&
                                  checkAllLoaded
                                  ? ('all' as keyof SelectDisplayOption)
                                  : (selectedOption?.name === 'none' ||
                                        selectedOption.name === 'all') &&
                                      !checkAllLoaded
                                    ? ('none' as keyof SelectDisplayOption)
                                    : (selectedOption?.name as keyof SelectDisplayOption)
                                : checkAllLoaded
                                  ? ('all' as keyof SelectDisplayOption)
                                  : ('none' as keyof SelectDisplayOption)
                          }
                        />
                      )}
                    >
                      {({ setVisible }) => (
                        <Button as='unstyled' onClick={() => setVisible(true)}>
                          <Icon icon='carat' iconSize='8px' />
                        </Button>
                      )}
                    </Popover>
                  ) : null}
                </div>
              </Cell>
            ) : (
              headerChildren?.jsx
            )}

            {/********************************** HEADER COLUMNS *********************************/}
            {config.map((th: ConfigItem, i: number) => {
              const lastLeftStickyColumn =
                  // If headerChildren exist, then we need to subtract the number of headerChildren and then we naturally subtract 1 to account for the 0 index in arrays.
                  stickyTableConfig?.left
                    ? stickyTableConfig?.left - (headerChildren?.count ?? 0) - 1
                    : 0,
                firstRightStickyColumn =
                  config &&
                  stickyTableConfig?.right &&
                  config.length - stickyTableConfig?.right
              return (
                <Cell
                  key={`${th.label}_${i}`}
                  style={{
                    ...th.style,
                    ...(!th?.isButton
                      ? {
                          minWidth: i > 0 ? equalWidthAmount : '200px', // The first column will be at least 200px wide. This is the minimum width that UX wanted.
                        }
                      : {}),
                  }}
                  className={`${
                    lastLeftStickyColumn === i ? styles.lastStickyCellLeft : ''
                  } ${
                    Number(lastLeftStickyColumn) + 1 === i
                      ? styles.extraLeftPadding
                      : ''
                  } ${
                    firstRightStickyColumn === i
                      ? `${styles.firstStickyCellRight} ${styles.extraLeftPadding}`
                      : ''
                  } ${th.className ?? ''}`.trim()}
                >
                  {sort &&
                  sortBy &&
                  ((!th.noSort && hasData) ||
                    th.filter ||
                    (th?.columnHeaderSubContent && hasData)) ? (
                    <SortColumn
                      label={th.label}
                      {...(th.options
                        ? { options: th.options }
                        : { propName: th.name ?? '' })}
                      sorter={sort}
                      customClass={th.className}
                      active={sortBy}
                      localStorageName={localStorageName}
                      lowerCaseParam={th.lowerCaseParam}
                      tooltip={th.tooltip}
                      filter={th.filter}
                      activeFilters={activeFilters}
                      noSort={th.noSort}
                      columnHeaderSubContent={th.columnHeaderSubContent}
                      extraSpaceWhenStickyColumn={showStickyClasses}
                      beta={th.beta}
                      tableHeaderActiveFilters={
                        tableHeaderProps?.columnFilterProps?.activeFilters
                      }
                      twoLineLabel={twoLineLabel}
                    />
                  ) : (
                    <UnsortedColumn
                      label={th.label}
                      tooltip={th.tooltip}
                      className={th.className ?? ''}
                      extraSpaceWhenStickyColumn={showStickyClasses}
                      beta={th.beta}
                      twoLineLabel={twoLineLabel}
                    />
                  )}
                </Cell>
              )
            })}

            {/**************************** NESTED ROWS SELECTOR COLUMN **************************/}
            {(nestedRowProps || hasExpandableContent) && !customColumnProps && (
              <Cell
                className={`${styles.actionItems} ${customColumnStickyStyles(
                  2,
                  stickyRightCount,
                )}`}
              />
            )}

            {/************************* CUSTOM COLUMNS ADJUSTMENT COLUMN ************************/}
            {customColumnProps && (
              <Cell
                className={`${styles.actionItems} ${customColumnStickyStyles(
                  1,
                  stickyRightCount,
                )}`}
              >
                <CustomizeTableColumns {...customColumnProps} />
              </Cell>
            )}
          </Row>

          {/*************************************************************************************/}
          {/********************************* DATA ROWS *****************************************/}
          {/*************************************************************************************/}
          {!loading ? (
            hasData && (
              <>
                {displayData?.map((_data, rowIndex) => {
                  const groupKey = _data?.groupDataKey
                  const hasTotalRow = totalRowKey && _data?.[totalRowKey]
                  if (
                    typeof groupKey === 'string' &&
                    collapsedGroups?.includes(groupKey) &&
                    !_data?.isGroupHeader
                  ) {
                    // Toggle classes based on this prop to expand/collapse accordion
                    _data.groupAccordion = {
                      ..._data.groupAccordion,
                      isCollapsed: true,
                    } as groupAccordionType
                  } else if (_data?.groupAccordion) {
                    _data.groupAccordion = {
                      ..._data.groupAccordion,
                      isCollapsed: false,
                    } as groupAccordionType
                  }
                  return (
                    <React.Fragment key={`${rowIndex}_${_data?.[dataKey]}`}>
                      {/* Set up a new row and cell set for Header rows...
                            these rows can have their own styles that are more easily styled
                            and the cells will not have any values in them so they can't display incorrect
                            values, buttons or anything else that has been add to the config */}
                      {showGroups && _data?.isGroupHeader ? (
                        <Row
                          id={`header-${
                            (typeof _data.groupHeader === 'function'
                              ? _data.groupHeader(checkedBoxes.length)
                              : _data.groupHeader) ?? 'groupHeader'
                          }`}
                          // Needed for SortableJS to know that this row is not draggable
                          className='dnd-non-draggable'
                        >
                          {/***************************** CHECKBOX ****************************/}
                          {hasCheckboxes && (
                            <Cell
                              className={`${color[_data.type ?? 'gray']} ${
                                backgroundColor[_data.type ?? 'gray']
                              } ${styles.checkboxWidth}`}
                              style={{
                                ...headerStyle(false, !!totalRowKey),
                              }}
                            />
                          )}

                          {/************************ MAIN TABLE COLUMNS ************************/}
                          {config.map((_config, index) => {
                            return (
                              <Cell
                                key={`${index}
                                  ${
                                    (typeof _data.groupHeader === 'function'
                                      ? _data.groupHeader(checkedBoxes.length)
                                      : _data.groupHeader) ?? 'groupHeader'
                                  }`}
                                className={`${color[_data.type ?? 'gray']} ${
                                  backgroundColor[_data.type ?? 'gray']
                                } ${hasStickyColumnStyle({
                                  colIndex: hasCheckboxes
                                    ? index + 2
                                    : index + 1,
                                  stickyLeftColumn: getStickyLeftCount(),
                                  stickyRightColumn: stickyTableConfig?.right,
                                  tableHeadersLength: hasCheckboxes
                                    ? config.length + 1
                                    : config.length,
                                })}`}
                                style={{
                                  ...headerStyle(
                                    (stickyTableConfig?.left ?? 1) - index > 0,
                                    !!totalRowKey,
                                  ),
                                  ...(index === config.length - 1 &&
                                  _data.includeClearButton
                                    ? { textAlign: 'right' }
                                    : {}),
                                  ...(_config.cell?.style ?? {}),
                                  ...(!_config?.isButton
                                    ? {
                                        minWidth:
                                          index > 0
                                            ? equalWidthAmount
                                            : '200px', // The first column will be at least 200px wide. This is the minimum width that UX wanted.
                                      }
                                    : {}),
                                }}
                              >
                                {/* FIRST COLUMN -- CHECKBOXES */}
                                {index === 0 ? (
                                  typeof _data.groupHeader === 'function' ? (
                                    _data.groupHeader(
                                      checkBoxesHaveTotalRowKey
                                        ? checkedBoxes?.length - 1
                                        : checkedBoxes.length,
                                    )
                                  ) : (
                                    <span className={styles.displayFlex}>
                                      {_data.groupHeader}
                                      {_data.tooltipContent && (
                                        <Tooltip
                                          position='auto'
                                          tooltipContent={_data.tooltipContent}
                                        >
                                          <Icon
                                            icon='info'
                                            className={styles.marginLeftIcon}
                                            iconSize='12px'
                                            color='dark-blue'
                                          />
                                        </Tooltip>
                                      )}
                                    </span>
                                  )
                                ) : (
                                  ''
                                )}

                                {/* MIDDLE COLUMNS -- DISPLAY HEADER DATA VALUES (if they exist)  */}
                                {index > 0 &&
                                  index !== config.length - 1 &&
                                  _data?.displayHeaderData && (
                                    <div className={styles.fontWeightSemiBold}>
                                      {_config.cell.children(_data)}
                                    </div>
                                  )}

                                {/* LAST COLUMN -- ACTION BUTTON -- CLEAR or CUSTOM JSX */}
                                {index === config.length - 1 &&
                                _data?.customClearButton ? (
                                  <>{_data?.customClearButton}</>
                                ) : (
                                  index === config.length - 1 &&
                                  _data.includeClearButton && (
                                    <Button
                                      as='button'
                                      styleType='text-blue'
                                      onClick={() => {
                                        setCheckedBoxes([])
                                        setCheckAllLoaded(false)
                                        _data?.clearCallout?.()
                                      }}
                                    >
                                      {c('clear')}
                                    </Button>
                                  )
                                )}

                                <div className={styles.flexDisplay}>
                                  {index === config.length - 1 &&
                                    _data?.lastColumn && (
                                      <>{_data?.lastColumn}</>
                                    )}
                                  {/* Icon for accordion */}
                                  {index === config.length - 1 &&
                                    _data?.groupAccordion && (
                                      <Button
                                        as='unstyled'
                                        onClick={() =>
                                          _data?.groupAccordion &&
                                          updateAccordion(
                                            _data.groupAccordion.groupKey,
                                          )
                                        }
                                      >
                                        <Icon
                                          icon='caretDown'
                                          className={`${
                                            typeof groupKey === 'string' &&
                                            !collapsedGroups?.includes(
                                              _data.groupAccordion.groupKey ||
                                                groupKey,
                                            )
                                              ? styles.flipped
                                              : ''
                                          }`}
                                          iconSize='16px'
                                        />
                                      </Button>
                                    )}
                                </div>
                              </Cell>
                            )
                          })}

                          {/********************** NESTED ROW SPACER CELL **********************/}
                          {nestedRowProps && nestedDataKey && (
                            <Cell
                              className={`${
                                backgroundColor[_data.type ?? 'gray']
                              } ${
                                styles.actionItems
                              } ${customColumnStickyStyles(
                                2,
                                stickyRightCount,
                              )}`}
                              style={headerStyle(false, !!totalRowKey)}
                            />
                          )}

                          {/********************* CUSTOM COLUMN SPACER CELL *********************/}
                          {customColumnProps && (
                            <Cell
                              className={`${
                                backgroundColor[_data.type ?? 'gray']
                              } ${customColumnStickyStyles(
                                1,
                                stickyRightCount,
                              )}`}
                              style={headerStyle(false, !!totalRowKey)}
                            />
                          )}
                        </Row>
                      ) : (
                        <Row
                          key={`${_data?.[dataKey]}-${rowIndex}`}
                          {...(hasTotalRow ? { ref: totalRowKeyRef } : {})}
                          style={
                            _data?.groupDataKey
                              ? _data?.groupAccordion?.isCollapsed
                                ? { display: 'none' }
                                : {}
                              : {}
                          }
                          // Needed for SortableJS to know that this row is not draggable
                          className={`${
                            hasTotalRow
                              ? 'dnd-non-draggable'
                              : hasCheckboxes &&
                                  (checkAllLoaded || hasValue(isChecked(_data)))
                                ? // Used to drag and drop all selected rows at once.
                                  'dnd-selected'
                                : // Case where we drag and drop items one by one.
                                  ''
                          } ${
                            hasExpandableContent &&
                            expandedAccordionContent?.id === _data?.[dataKey]
                              ? styles.bgc
                              : ''
                          }`}
                          // Useful when Drag and Drop is implemented using SortableJS to let consuming component identify the row via HTML: dataset: {uid: _data?.[dataKey]}
                          data-uid={_data?.[dataKey]}
                        >
                          {totalRowKey &&
                            _data?.[totalRowKey] &&
                            hasCheckboxes && (
                              <Cell
                                className={`${color[_data.type ?? 'gray']} ${
                                  backgroundColor[_data.type ?? 'gray']
                                }
                              ${styles.checkboxWidth}`}
                                style={headerStyle(false, false)}
                              />
                            )}

                          {/***************************** CHECKBOX ****************************/}
                          {hasCheckboxes && !hasTotalRow && (
                            <Cell
                              key={`checkbox-${_data?.[dataKey]}`}
                              className={styles.checkboxCell}
                              onClick={() => {
                                if (!disableCheckboxes?.(_data)) {
                                  handleCheck({
                                    checkboxValue: !hasValue(isChecked(_data)),
                                    checkedItemData: _data,
                                    name: `checkbox-${_data?.[dataKey]}`,
                                  })
                                }
                              }}
                            >
                              {disableCheckboxes?.(_data) ? (
                                <Tooltip
                                  tooltipContent={c(
                                    'tooltipCheckboxTableDisabled',
                                  )}
                                >
                                  <Checkbox
                                    name={`checkbox-${_data?.[dataKey]}`}
                                    stateName='table_data'
                                    checked={
                                      checkAllLoaded ||
                                      hasValue(isChecked(_data))
                                    }
                                    callout={(name, value) =>
                                      handleCheck({
                                        checkboxValue: value,
                                        checkedItemData: _data,
                                        name,
                                      })
                                    }
                                    label=''
                                    hideLabel
                                    disabled
                                  />
                                </Tooltip>
                              ) : (
                                <Checkbox
                                  name={`checkbox-${_data?.[dataKey]}`}
                                  stateName='table_data'
                                  checked={hasValue(isChecked(_data))}
                                  callout={(name, value) =>
                                    handleCheck({
                                      checkboxValue: value,
                                      checkedItemData: _data,
                                      name,
                                    })
                                  }
                                  label=''
                                  hideLabel
                                />
                              )}
                            </Cell>
                          )}

                          {/************************ MAIN TABLE COLUMNS ************************/}
                          {config.map((c, index) => {
                            const cells = []
                            if (c.options) {
                              c.options.forEach((co) => {
                                cells.push(co.name)
                              })
                            }
                            if (c.name) {
                              cells.push(c.name)
                            }
                            return (
                              <Cell
                                key={`${
                                  _data?.[dataKey] ||
                                  `missing_${String(dataKey)}`
                                }-${c.name}-${index}`}
                                className={`${
                                  sortBy
                                    ? activeCellClass({
                                        cells,
                                        activeName: sortBy.prop,
                                      })
                                    : ''
                                } ${hasStickyColumnStyle({
                                  colIndex: hasCheckboxes
                                    ? index + 2
                                    : index + 1,
                                  stickyLeftColumn: getStickyLeftCount(),
                                  stickyRightColumn: stickyTableConfig?.right,
                                  tableHeadersLength: hasCheckboxes
                                    ? config.length + 1
                                    : config.length,
                                })} ${
                                  hasTotalRow
                                    ? styles.lighterGrayBackground
                                    : ''
                                } ${
                                  c.cell.className
                                    ? typeof c.cell.className === 'string'
                                      ? c.cell.className
                                      : c.cell.className(_data)
                                    : ''
                                }
                                 `}
                                style={{
                                  ...(c?.cell?.style ?? {}),
                                }}
                              >
                                {c.cell.children(
                                  { ..._data, sortProp: sortBy?.prop },
                                  rowIndex,
                                )}
                              </Cell>
                            )
                          })}

                          {/********************* CUSTOM COLUMN SPACER CELL *********************/}
                          {customColumnProps &&
                            !nestedRowProps &&
                            !hasExpandableContent && (
                              <Cell
                                className={`${
                                  hasTotalRow
                                    ? styles.lighterGrayBackground
                                    : ''
                                } ${
                                  styles.actionItems
                                } ${customColumnStickyStyles(
                                  nestedRowProps ? 2 : 1,
                                  stickyRightCount,
                                )}`}
                              />
                            )}

                          {/********************** NESTED ROW OPEN/CLOSE CARET **********************/}
                          {(nestedRowProps && nestedDataKey) ||
                          hasExpandableContent ? (
                            <Cell
                              className={`${
                                styles.actionItems
                              } ${customColumnStickyStyles(
                                1,
                                stickyRightCount,
                              )} ${
                                hasTotalRow ? styles.lighterGrayBackground : ''
                              }`}
                            >
                              {nestedDataKey &&
                              (!nestedRowProps?.showCaret ||
                                nestedRowProps?.showCaret(_data)) &&
                              !hasTotalRow ? (
                                <Button
                                  as='unstyled'
                                  onClick={() =>
                                    handleNestedData(
                                      _data,
                                      selectedNestedRowData.includes(
                                        _data[nestedDataKey],
                                      ),
                                    )
                                  }
                                >
                                  <Icon
                                    icon='caretDown'
                                    className={`${
                                      selectedNestedRowData.includes(
                                        _data[nestedDataKey],
                                      )
                                        ? styles.flipped
                                        : ''
                                    }`}
                                    iconSize='16px'
                                  />
                                </Button>
                              ) : hasExpandableContent && !hasTotalRow ? (
                                <Button
                                  id={expandableAccordionId}
                                  as='unstyled'
                                  onClick={() =>
                                    expandedAccordionContent?.callout(
                                      _data[dataKey] ===
                                        expandedAccordionContent.id
                                        ? null
                                        : _data[dataKey],
                                    )
                                  }
                                >
                                  <Icon
                                    icon='caretDown'
                                    className={`${
                                      expandedAccordionContent?.id ===
                                      _data?.[dataKey]
                                        ? styles.flipped
                                        : ''
                                    }`}
                                    iconSize='16px'
                                  />
                                </Button>
                              ) : null}
                            </Cell>
                          ) : null}
                        </Row>
                      )}

                      {/***************************** Expanded Content **************************/}
                      {hasExpandableContent &&
                      expandedAccordionContent?.id === _data?.[dataKey] ? (
                        expandedAccordionContent.isLoading ? (
                          <ListLoading />
                        ) : (
                          // Row & Cell components from library prvents this row to span entirely and wont allow it to be sticky.
                          // Hence we are building custom row and cells from raw HTML.
                          <tr
                            className={`${styles.positionRelative} sticky-table-row`}
                          >
                            <td
                              ref={expandedAccordionContentRef}
                              className={styles.positionRelative}
                              colSpan={100} // 100 is used to make sure the row spans the entire width irrespective of no of columns assuming we will necver have 100 columns active at any point. The we resize to window size in the below div.
                            >
                              <div
                                style={{
                                  width: `${expandedContentWidth}px`,
                                }}
                                className={styles.expandedRowContainer}
                              >
                                {expandedAccordionContent?.content}
                              </div>
                            </td>
                          </tr>
                        )
                      ) : null}

                      {/**************************** NESTED ROW DATA ****************************/}
                      {nestedRowProps &&
                        nestedDataKey &&
                        !_data?.groupHeader && (
                          <NestedRow
                            data={_data}
                            hasCheckboxes={hasCheckboxes}
                            nestedDataKey={nestedDataKey}
                            nestedRowProps={nestedRowProps}
                            config={config}
                            {...(sortBy ? { sortBy } : {})}
                            customColumnProps={customColumnProps}
                            stickyTableConfig={stickyTableConfig}
                            selectedNestedRowData={selectedNestedRowData}
                            totalRowKey={totalRowKey}
                            showStickyClasses={showStickyClasses}
                            stickyRightCount={stickyRightCount}
                          />
                        )}
                    </React.Fragment>
                  )
                })}
                {hasMore && (
                  <div
                    ref={infiniteLoadRef}
                    className={styles.listLoadingWrapper}
                  >
                    <ListLoading />
                  </div>
                )}
              </>
            )
          ) : (
            <div className={styles.listLoadingWrapper}>
              <ListLoading longList={!shortListLoading} />
            </div>
          )}
        </StickyTable>
        {/* show gradient for more records */}
        {hasData && <TableGradient showStickyClasses={showStickyClasses} />}
      </StickyTableContainer>
    </div>
  )
}

export default DesktopTable
